import {AzureBaseMap} from "./map/module/AzureBaseMap";
import {AzureSelect} from "./map/module/AzureSelect";
import {AzureEdit} from "./map/module/AzureEdit";
import {Render} from "./map/module/render/Render";
import {ScheduleRender} from "@esWebAPI/es/map/module/render/ScheduleRender";
import {TerritoryRender} from "@esWebAPI/es/map/module/render/TerritoryRender";
import {DayTripRender, DirectionsModule} from "@esWebAPI/es/map/module/render/DayTripRender";
import {AzureOverlay} from "./map/module/AzureOverlay";
import {AzurePopup} from "./map/module/AzurePopup";
import ScheduleModel from "./map/ScheduleModel";
import TerritoryModel from "./map/TerritoryModel";
import {FeatureViewModel} from "@esWebAPI/es/map/FeatureViewModel";
import {TerritoryLayerCollection} from "@esWebAPI/es/map/layer/territory/TerritoryLayerCollection";
import Layer from "./map/module/Layer";
import Controls from "./map/layer/Controls";
import MetaModule from "./map/module/MetaModule";
import {ScheduleModule} from "./map/module/ScheduleModule";
import SpinnerModule from "./map/module/SpinnerModule";
import RoutingModule from "./map/RoutingModule";
import VectorModule from "./map/vector/vectorModule";

import type {IEventChannel, InitMapParams} from "@esWebAPI/es/model/Models";
import type MobileApiRequestBroker from "@esWebAPI/es/MobileApiRequestBroker";
import type {GeoMap, TempGeoMap} from "@esWebAPI/es/model/GeoMap";
import {ThematicStylerFactory} from "@esWebAPI/es/map/styler/ThematicStylerFactory";
import { Schedule } from "./model/Schedule";
import DirectionsModel from "@esWebAPI/es/map/DirectionsModel";

export class Map {
    analysis;
    baseMap: AzureBaseMap;
    render: Render;
    select: AzureSelect;
    popup: AzurePopup;
    overlay: AzureOverlay;
    eventChannel: IEventChannel;
    featureViewModel: FeatureViewModel;
    layer: Layer;
    directions: DirectionsModel|undefined;
    schedule: ScheduleModule|undefined;
    territory: TerritoryModel;
    meta : MetaModule;
    tenant: any;
    edit: AzureEdit;
    spinner: SpinnerModule;

    constructor(parentElement: HTMLElement, initMapParams: InitMapParams, geoMap: GeoMap, analysis: any, schedule: Schedule|null,
        featureViewModel: FeatureViewModel, territoryModel: TerritoryModel, tenant: any, tenantLimits: any, tenantConfig: any,
        isOwnerOfGeoMap: boolean, geoMapShareStatus: any, isConfigurableGeoMap: boolean, isConfigurableParentGeoMap: boolean, isEditableGeoMap: boolean,
        isConfigurableWorkspace: boolean, parentGeoMapShareStatus: any, eventChannel: IEventChannel) {



        //TODO Remove territoryModel from LayerModule
        const layerModule = new Layer(featureViewModel, territoryModel);
        const baseMapModule = new AzureBaseMap(layerModule, territoryModel, parentElement, geoMap, eventChannel);
        const overlayModule = new AzureOverlay(baseMapModule);
        const territoryLayerCollection = territoryModel.hasTerritory() ? new TerritoryLayerCollection(baseMapModule, territoryModel, featureViewModel, overlayModule, initMapParams, eventChannel) : null;
        const selectModule = new AzureSelect(baseMapModule, layerModule, territoryLayerCollection, overlayModule, eventChannel);
        const editModule = new AzureEdit(baseMapModule, featureViewModel, selectModule, overlayModule, eventChannel);

        const hasDayTripRoute = layerModule.list().filter(lvm => lvm.getStyleSettingsObjRef().conditionalStyleSettings?.name === "route").length > 0;

        // Creating correct Renderer
        let render: Render;
        if (geoMap.$type.startsWith("Schedule") && schedule) {
            const scheduleModel = new ScheduleModel(schedule, featureViewModel, tenantLimits, tenantConfig);
            const scheduleModule = new ScheduleModule(baseMapModule, layerModule, scheduleModel, editModule);
            const stylerFactory = new ThematicStylerFactory(undefined, scheduleModule);
            render = new ScheduleRender(baseMapModule, featureViewModel, scheduleModule, initMapParams, overlayModule, stylerFactory, eventChannel);
            this.schedule = scheduleModule;
        } else if(geoMap.$type.startsWith('Territory') && territoryLayerCollection) {
            const stylerFactory = new ThematicStylerFactory(territoryModel);
            render = new TerritoryRender(baseMapModule, featureViewModel, selectModule, territoryModel, territoryLayerCollection, initMapParams, overlayModule, stylerFactory, eventChannel);
        } else if ((hasDayTripRoute || initMapParams.useDirectionsRender)) {
            this.directions = new DirectionsModel();
            const directionModule = new DirectionsModule(this.directions);
            const stylerFactory = new ThematicStylerFactory(undefined, directionModule);
            render = new DayTripRender(baseMapModule, featureViewModel, directionModule, analysis, layerModule, initMapParams, overlayModule, stylerFactory, eventChannel);
        } else {
            render = new Render(baseMapModule, featureViewModel, analysis, initMapParams, overlayModule, new ThematicStylerFactory(), eventChannel);
        }
        render.startLoad();

        this.analysis = analysis;
        this.baseMap = baseMapModule;
        this.render = render;
        this.select = selectModule;
        this.popup = new AzurePopup(baseMapModule, eventChannel);
        this.overlay = overlayModule;
        this.eventChannel = eventChannel;
        this.featureViewModel = featureViewModel;
        this.layer = layerModule;
        this.territory = territoryModel;
        this.tenant = tenant;
        this.meta = new MetaModule(geoMap, isOwnerOfGeoMap, eventChannel, featureViewModel, geoMapShareStatus, isConfigurableGeoMap,
            isConfigurableParentGeoMap, isEditableGeoMap, isConfigurableWorkspace, analysis, parentGeoMapShareStatus);
        this.edit = editModule;
        this.spinner = new SpinnerModule(parentElement, eventChannel);

        const controls = new Controls(baseMapModule, overlayModule, eventChannel);
        controls.appendTo(parentElement);
    }

    /**
     * Builds the mapState for aiAssistant (or something else maybe? e.g. could be good debug info).
     */
    public getMapState(): any {
        const territoryModel = this.layer.getTerritoryModel();
        const datasets = this.layer.getLayerViewsClone()
            .filter(dataset => dataset.geomType == "POINT" || dataset.geomType == "POLYGON")
            .map(dataset => {
                const layerViewModel = this.layer.getById(dataset.id);
                const layerInfo = layerViewModel?.getLayerInfo();
                const recordCount = layerInfo?.featureInfo?.count;
                // Note: getLayerInfo() restricts attributes to the view-configured attributeNames,
                // which can be narrower than the full feature-source schema query_dataset operates
                // on. Acceptable for now - revisit if the assistant misses real attributes.
                // (Territory columns like Locked/assignment are NOT dataset attributes either way -
                // they live on the territory model.)
                const nonInternalAttributes = layerInfo?.attributes?.filter((a: any) => !a.internal) ?? [];
                // Full schema (name + type) so the assistant can filter/aggregate on ANY
                // attribute, including numeric ones. A separate STRING-only searchableAttributes
                // list used to be emitted too, but it was fully derivable from `attributes`
                // (type === "STRING") and having two overlapping lists confused the LLM.
                const attributes = nonInternalAttributes.map((a: any) => ({ name: a.name, type: a.attrType }));

                // Active (persisted) filter criteria, in the same grammar the assistant uses for
                // propose_layer_view_filter. Transient criteria is excluded - it is ephemeral UI state.
                // A territory map's base AREA layer is not user-filterable here, so we never report a filter
                // on it: any criteria sitting on it is a leftover the app does not honour, and reporting it
                // makes the assistant reason about a phantom "the counties are filtered" state.
                const filter = territoryModel?.isBaseLayer(dataset.id)
                    ? undefined
                    : (layerViewModel?.getCriteriaClone() ?? undefined);

                return {
                    id: dataset.id,
                    name: dataset.title,
                    enabled: dataset.enabled,
                    type: dataset.geomType,
                    // Only POINT datasets can be map-filtered via propose_layer_view_filter (the points
                    // filter panel). POLYGON/boundary datasets are still listed so the assistant can
                    // query_dataset / reason about coverage, but they are not filterable on the map here.
                    filterable: dataset.geomType === "POINT",
                    record_count: recordCount,
                    attributes: attributes.length > 0 ? attributes : undefined,
                    filter: filter,
                };
            });

        const page = this.meta.isTerritory() ? "territories" : "map"; // todo: refactor to use geoMap.$type?
        let basemapType = this.meta.getBaseMapType() || "roadmap";

        let territoryConfig: any;
        if (page === "territories") {
            const territoryModel = this.layer.getTerritoryModel();
            if (territoryModel) {
                const meta = territoryModel.getTerritoryMetaObjRef();
                const type = territoryModel.isAccountOnly() ? "ACCOUNTS_ONLY" : "AREAS_AND_ACCOUNTS";
                const balanceAttributes: string[] = [];
                if (meta?.territoryDerivedAttributes) {
                    meta.territoryDerivedAttributes.forEach((da: any) => {
                        if (da.enabled && da.name) {
                            const displayName = da.title || da.name;
                            const source = this.describeBalanceAttributeSource(da);
                            balanceAttributes.push(source ? `${displayName} (${source})` : displayName);
                        }
                    });
                }
                territoryConfig = {
                    type: type,
                    territoryCount: territoryModel.getNumTerritoryFeatures(),
                    balanceAttributes: balanceAttributes,
                    baseLayerName: territoryModel.baseLayerView?.getTitle() ?? undefined,
                };
                // When a territory filter is active, show the visible subset so terAssist is aware.
                // Only report a filter when one is genuinely set (getFilter()), and compare WITHIN the
                // current hierarchy level: getNumTerritoryFeatures() counts every territory across all
                // levels, so on a hierarchical map the other levels would otherwise look like a filter
                // (e.g. "19 of 57 filtered out" when the user is simply viewing one level, unfiltered).
                const territoryFilter = territoryModel.getFilter();
                const hasFilter = !!territoryFilter && territoryFilter.length > 0;
                if (hasFilter) {
                    const levelTotal = territoryModel.getActive(false, true).length;          // current level (all, if flat)
                    const visibleTerritoryCount = territoryModel.getActive(true, true).length; // current level + active filter
                    if (visibleTerritoryCount !== levelTotal) {
                        territoryConfig.visibleTerritoryCount = visibleTerritoryCount;
                        territoryConfig.filteredOutCount = levelTotal - visibleTerritoryCount;
                    }
                }
            }
        }

        return {
            map: {
                id: this.meta.getId(),
                name: this.meta.getTitle(),
                page: page,
                basemapType: basemapType,
                territoryConfig: territoryConfig,
            },
            datasets: datasets,
            user_id: this.tenant ? (this.tenant.id + "_" + this.tenant.email) : undefined,
        };
    }

    /**
     * States what a balance attribute's values measure, resolved from its definition. The
     * user-chosen display name (e.g. "Count") has no unit fo measurement, so agent then
     * borrows whichever unit is nearby - typically the base areas ("19 ZIP codes" for a
     * 19-account territory). Null when the definition carries nothing describable.
     * see also: com/espatial/es/service/territory/TerritoryAnalysisService.java:726
     */
    private describeBalanceAttributeSource(da: any): string | null {
        const definition = da.definition;
        if (!definition) {
            return null;
        }
        if (definition.$type === "WorkIndexTerritoryDerivedAttributeDefinition") {
            return "weighted work index score, not a record count";
        }
        const items = definition.weightedItems;
        if (!items || items.length === 0) {
            return null;
        }
        const parts: string[] = [];
        for (const item of items) {
            const datasetTitle = this.layer.getByLayerMetaId(item.sourceLayerMetaId)?.getTitle() ?? "its source dataset";
            if (item.function === "COUNT" || item.sourceAttributeName === "ES_COUNT") {
                parts.push(`number of "${datasetTitle}" points per territory, NOT a count of base areas`);
            } else {
                parts.push(`sum of "${item.sourceAttributeName}" from "${datasetTitle}" per territory`);
            }
        }
        return parts.join("; ");
    }
}

export type MobileMap = Map & {api: MobileApiRequestBroker}

export default class MapModule {
    readonly EVENT = {
        onAddAddressSearchPoint : 1,
        onAddLayer : 1,
        onRemoveLayer : 1,
        onUpdateLayer : 1,
        onUpdateLayerIndex: 1,
        onSetActiveLayerViewId : 1,
        onUpdateLayerComplete: 1,

        onAddPopup : 1,
        onRemovePopup : 1,

        onBeginZoomToArea : 1,
        onFinishZoomToArea : 1,

        onAddAddressPoint : 1,
        onClearPopups : 1,
        onCreatedAddressPoint : 1,
        onDestroy : 1,

        onBeginUpdateFeature : 1, // TODO: consolidate or rename to differentiate: onBeginEditFeature, onBeginEditFeature
        onBeginEditFeature : 1,

        onUpdateFeature : 1, // TODO: consolidate or rename to differentiate: onUpdateFeature, onEditFeature
        onEditFeature : 1,

        onCreateFeature : 1,
        onRemoveFeature : 1,

        onCommitFeatureEdits : 1,
        onRevertFeatureEdits : 1,

        onFeaturePopupOpen  : 1,
        onFeaturePopupClose  : 1,
        onSelectFeature : 1,
        onDeselectFeature : 1,
        onSelectFeatureFoundMultipleFeatures: 1,
        onShowListFeatures : 1,
        onDestroySelectByGeom : 1,
        onClearSelections : 1,
        onSetSelectionEnabled: 1,
        onOverlayFeatureClick: 1,

        onFeatureViewModelUpdateLayer : 1,
        onFeatureViewUpdate : 1,

        onGeneratingTilesStart : 1,
        onGeneratingTilesFinish : 1,

        onSlowActionStart: 1,
        onSlowActionFinish: 1,

        onLocationLock : 1,
        onSetBaseMap : 1,
        onInitWeatherOverlay : 1,
        onSetWeatherOverlay : 1,
        onSetCenter: 1,
        onSetResolution : 1,

        onThematicActivate : 1,
        onThematicDeactivate : 1,
        onThematicReset : 1,

        onUpdate : 1,

        onUpdateLocation: 1,
        onUpdateSizePx : 1,
        onUpdateView : 1,
        onZoomOutPopupOpen: 1,
        onTemporaryAddStop : 1,
        onTouchEnd: 1,
        onUpdateFeatureSourceCriteria : 1,

        onTerritoryLayerLoadStart : 1,
        onTerritoryLayerLoadFinish: 1,
        onTerritoryBaseLayerLoadProgress: 1,
        onTerritoryLayerCollectionLoadFinish: 1,
        onTerritoryFeaturePlusMinusClick: 1,
        onTerritoryChange : 1,
        onTerritoryCurrentChange: 1, // fired when selected territory changes
        onTerritoryLabelsUpdate: 1,
        onTerritoryLevelChange: 1,
        onTerritoryParentChange: 1,
        onTerritoryFilterChange: 1,
        onTerritoryAttributeChange: 1,
        onTerritoryDrawStart: 1,
        onTerritoryDrawFinish: 1,
        onTerritoryGenerateOutlinesStart: 1,
        onTerritoryGenerateOutlinesFinish: 1,
        onTerritoryOutlinesLayerUpdate: 1,
        onTerritoryPointMappingUpdate: 1,
        onSelectionChange: 1,

        onSyncDatasetFinishAndFeatureViewModelUpdated : 1, // fired after a map syncs all datasets AND the map featuremodel has subsequently been updated for all syncd layers

        onShowLoadingOverlay: 1,
        onHideLoadingOverlay: 1,

        onLeaderLineLabelsRendered: 1, // onEsMapFullyRendered

        onGenerateImagesStart: 1,
        onGenerateImagesFinish: 1
    };

    readonly params = {
        baseMapType : "roadmap"
    } as InitMapParams;

    readonly vector: VectorModule;
    readonly routing: RoutingModule;

    constructor() {
        this.vector = new VectorModule();
        this.routing = new RoutingModule();
    }

    /**
     *  Create a map. Return a promise with the map as a parameter
     *  when the map is ready
     *  @param parentElement
     *  @param directionsModel DirectionModel for DayTrip
     *  @param params
     */
    public create(parentElement: HTMLElement, params: InitMapParams): JQuery.Promise<Map> {
        if (!parentElement) {
            parentElement = document.body;
        }

        if (!params) {
            params = $.extend(true, es.util.URLON.fromUrl(location.search), es.map.params);
        }

        return this.initMap(parentElement, params);
    }

    private initMap(parentElement: HTMLElement, initMapParams: InitMapParams): JQuery.Promise<Map> {
        return es.api.geoMap.get(initMapParams.geoMapId).then((geoMap) => {
            const eventChannel = es.util.createEventChannel(es.map.EVENT, "es.map");
            const featureViewModel = new FeatureViewModel(geoMap.id, eventChannel);
            const territoryModel = new TerritoryModel(featureViewModel, eventChannel);

            const geoMapParentId = geoMap.$type.endsWith('TempGeoMap') ? (geoMap as TempGeoMap).parentId : undefined;

            return Promise.all([
                featureViewModel.onReady,
                !es.isPublicAccess ? es.api.security.isOwnerOfGeoMap(geoMap.id) : !es.isPublicAccess,
                es.isPublicAccess ? null : es.api.security.getGeoMapShareStatus([geoMap.id]),
                es.isPublicAccess ? false : es.api.security.isConfigurableGeoMap(geoMap.id),
                es.isPublicAccess || !geoMapParentId ? false : es.api.security.isConfigurableGeoMap(geoMapParentId),
                es.isPublicAccess ? false : es.api.security.isEditableGeoMap(geoMap.id),
                es.isPublicAccess ? false : es.api.security.isConfigurableWorkspace(geoMap.workspaceId),
                geoMap.$type.startsWith('Schedule') ? es.api.schedule.getByGeoMapId(geoMap.id)  : null,
                es.api.tenant.get(),
                geoMap.$type.startsWith('Schedule') ? es.api.tenant.getLimits() : null,
                geoMap.$type.startsWith('Schedule') ? es.api.tenant.getTenantConfig() : null,
                es.api.analysis.getByGeoMapId(geoMap.id),
                geoMapParentId ? es.api.security.getGeoMapShareStatus([geoMapParentId]) : null,
                territoryModel.loadComplete
            ]).then(([featureViewModel, isOwnerOfGeoMap, geoMapShareStatus, isConfigurableGeoMap, isConfigurableParentGeoMap,
                    isEditableGeoMap, isConfigurableWorkspace, schedule, tenant, tenantLimits, tenantConfig, analysis,
                    parentGeoMapShareStatus]) => {
                const map = new Map(parentElement, initMapParams, geoMap, analysis, schedule, featureViewModel, territoryModel, tenant, tenantLimits, tenantConfig,
                    isOwnerOfGeoMap, geoMapShareStatus, isConfigurableGeoMap, isConfigurableParentGeoMap, isEditableGeoMap, isConfigurableWorkspace,
                    parentGeoMapShareStatus, eventChannel);

                return (territoryModel.hasTerritory() ? territoryModel.loadComplete : es.util.deferred.resolved()).then(() => {
                    return map;
                });
            });
        });
    }
}
